/**
 * Application Routes
 * Central routing configuration for the entire application
 */

import { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { QuizProvider } from '@/contexts/QuizContext';
import ProtectedRoute from '@/components/auth/ProtectedRoute';
import RequireEmailConfirmed from '@/components/auth/RequireEmailConfirmed';
import { CuratorProfileSkeleton } from '@/components/curator/CuratorProfileSkeleton';
import { Profile101Skeleton } from '@/components/profile/Profile101Skeleton';
import { FavoritesLoadingSkeleton } from '@/components/favorites/FavoritesLoadingSkeleton';
import { ChatListLoadingSkeleton } from '@/components/chat/ChatListLoadingSkeleton';

// Quiz
import QuizRunner from '@/components/quiz/QuizRunner';

// Maintenance guard
import { MaintenanceGuard } from './MaintenanceGuard';

// Route configurations
import {
  authRoutes,
  onboardingRoutes,
  catalogRoutes,
  profileRoutes,
  adsRoutes,
  chatRoutes,
  quizRoutes,
  adminRoutes,
  miscRoutes,
} from './routes.config';

// Critical path pages - eagerly loaded
import Splash from '@/pages/Splash';
import Login from '@/pages/auth/Login';
import Signup from '@/pages/auth/Signup';
import MainCatalog from '@/pages/MainCatalog';
import NotFound from '@/pages/NotFound';

// Loading fallback for lazy components
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-background">
    <div className="animate-pulse flex flex-col items-center gap-4">
      <div className="h-12 w-12 rounded-full bg-muted" />
      <div className="h-4 w-32 rounded bg-muted" />
    </div>
  </div>
);

// Helper component for lazy-loaded routes
function LazyRoute({ Component }: { Component: React.LazyExoticComponent<React.ComponentType<unknown>> }) {
  return (
    <Suspense fallback={<PageLoader />}>
      <Component />
    </Suspense>
  );
}

// Helper component for protected lazy-loaded routes (auth required)
function ProtectedLazyRoute({ Component }: { Component: React.LazyExoticComponent<React.ComponentType<unknown>> }) {
  return (
    <ProtectedRoute>
      <Suspense fallback={<PageLoader />}>
        <Component />
      </Suspense>
    </ProtectedRoute>
  );
}

// Helper component for routes requiring auth + email confirmation
function EmailProtectedLazyRoute({ Component }: { Component: React.LazyExoticComponent<React.ComponentType<unknown>> }) {
  return (
    <ProtectedRoute>
      <RequireEmailConfirmed>
        <Suspense fallback={<PageLoader />}>
          <Component />
        </Suspense>
      </RequireEmailConfirmed>
    </ProtectedRoute>
  );
}

export function AppRoutes() {
  return (
    <MaintenanceGuard>
      <Routes>
        {/* ========== CRITICAL ROUTES (eager loaded) ========== */}
        <Route path="/" element={<Splash />} />
        <Route path="/splash" element={<Splash />} />
        <Route path="/catalog" element={<MainCatalog />} />

        {/* ========== AUTH ROUTES ========== */}
        <Route path="/auth/login" element={<Login />} />
        <Route path="/auth/signup" element={<Signup />} />
        <Route path="/auth/forgot-password" element={<LazyRoute Component={authRoutes.ForgotPassword} />} />
        <Route path="/auth/reset-password" element={<LazyRoute Component={authRoutes.ResetPassword} />} />
        <Route path="/auth/confirm" element={<LazyRoute Component={authRoutes.EmailSent} />} />
        <Route path="/auth/email-confirmation" element={<LazyRoute Component={authRoutes.EmailConfirmation} />} />
        <Route path="/auth/password-reset-confirm" element={<LazyRoute Component={authRoutes.PasswordResetConfirmation} />} />
        <Route path="/auth/callback" element={<LazyRoute Component={authRoutes.AuthCallback} />} />
        <Route path="/auth/v1/verify" element={<LazyRoute Component={authRoutes.AuthCallback} />} />

        {/* ========== ONBOARDING ROUTES ========== */}
        <Route path="/onboarding/role" element={<LazyRoute Component={onboardingRoutes.Onboarding110} />} />
        <Route path="/welcome" element={<LazyRoute Component={onboardingRoutes.OnboardingCarousel} />} />
        <Route path="/onboarding/:step" element={<LazyRoute Component={onboardingRoutes.OnboardingCarousel} />} />

        {/* ========== QUIZ ROUTES ========== */}
        <Route path="/quiz/intro" element={<Navigate to="/quiz" replace />} />
        <Route
          path="/quiz/*"
          element={
            <QuizProvider>
              <QuizRunner />
            </QuizProvider>
          }
        />
        <Route path="/user/:userId/quiz" element={<ProtectedLazyRoute Component={quizRoutes.UserQuizProfilePage} />} />

        {/* ========== CHAT ROUTES (require email confirmation) ========== */}
        <Route 
          path="/chats" 
          element={
            <ProtectedRoute>
              <RequireEmailConfirmed>
                <Suspense fallback={<ChatListLoadingSkeleton />}>
                  <chatRoutes.ChatListScreen />
                </Suspense>
              </RequireEmailConfirmed>
            </ProtectedRoute>
          } 
        />
        <Route path="/chat/:roomId" element={<EmailProtectedLazyRoute Component={chatRoutes.ChatRoomScreen} />} />

        {/* ========== CATALOG & PET ROUTES ========== */}
        <Route path="/animal/:id" element={<LazyRoute Component={catalogRoutes.AnimalDetail} />} />
        <Route path="/pet/:id" element={<LazyRoute Component={catalogRoutes.PetDetail} />} />

        {/* ========== FAVORITES & ADS ROUTES ========== */}
        <Route 
          path="/favorites" 
          element={
            <ProtectedRoute>
              <Suspense fallback={<FavoritesLoadingSkeleton />}>
                <adsRoutes.FavoritesScreen />
              </Suspense>
            </ProtectedRoute>
          } 
        />
        <Route path="/my-ads" element={<ProtectedLazyRoute Component={adsRoutes.MyAdsScreen} />} />
        {/* Creating/editing ads requires email confirmation */}
        <Route path="/ads/create" element={<EmailProtectedLazyRoute Component={adsRoutes.ADS002CreateAd} />} />
        <Route path="/ads/create-ai" element={<EmailProtectedLazyRoute Component={adsRoutes.ADS002AICreate} />} />
        <Route path="/ads/edit/:id" element={<EmailProtectedLazyRoute Component={adsRoutes.ADS003EditAd} />} />

        {/* ========== PROFILE ROUTES ========== */}
        <Route
          path="/profile/:id"
          element={
            <Suspense fallback={<CuratorProfileSkeleton />}>
              <profileRoutes.CuratorProfile />
            </Suspense>
          }
        />
        <Route 
          path="/profile" 
          element={
            <ProtectedRoute>
              <Suspense fallback={<Profile101Skeleton />}>
                <profileRoutes.Profile101 />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route path="/profile/edit" element={<ProtectedLazyRoute Component={profileRoutes.Profile102} />} />
        <Route path="/profile-old" element={<LazyRoute Component={profileRoutes.Profile} />} />
        <Route path="/settings" element={<LazyRoute Component={profileRoutes.SettingsScreen} />} />
        <Route path="/about" element={<LazyRoute Component={profileRoutes.AboutScreen} />} />

        {/* ========== ADMIN & MODERATION ROUTES (require email confirmation) ========== */}
        <Route path="/moderation" element={<EmailProtectedLazyRoute Component={adminRoutes.ModerationScreen} />} />
        <Route path="/drugadm" element={<EmailProtectedLazyRoute Component={adminRoutes.AdminScreen} />} />

        {/* ========== MISC ROUTES ========== */}
        <Route path="/form" element={<LazyRoute Component={miscRoutes.Form} />} />
        <Route path="/done" element={<LazyRoute Component={miscRoutes.Done} />} />
        <Route path="/maintenance" element={<LazyRoute Component={miscRoutes.MaintenancePage} />} />

        {/* ========== FALLBACK ========== */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </MaintenanceGuard>
  );
}

export default AppRoutes;
